import { useEffect, useMemo } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000Label from "../../../../../u2000Core/components/formControls/label/U2000Label";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import {
  u3125CanModifyInvoice,
  u3125FormatBillingSourceDisplayAmount,
  u3125FormatCreditSourceLabel,
  u3125MaxCreditApplicationAmountForRow,
  u3125MaxCreditApplicationNonTaxableForRow,
  u3125MaxCreditApplicationTaxableForRow,
  u3125MergeCreditSourcesForDisplay,
  u3125RoundTaxAmount,
  u3125SelectableCreditSourcesForDialog,
  type U3125CreditSource,
  type U3125InvoiceFormRow,
} from "../../../../U3125Service";
import { u3125FormatCurrency } from "../../../../U3125";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";
import type { U3125CreditApplicationHandlerReturn } from "./U3125CreditApplicationHandlers";

interface U3125CreditApplicationEditDlgProps {
  u3125CreditApplicationHandler: U3125CreditApplicationHandlerReturn;
  parentFormHandlers: U3125FactureNestedParentHandlers;
  t: TFunction;
  NS: string;
}

function U3125CreditApplicationEditDlg({
  u3125CreditApplicationHandler,
  parentFormHandlers,
  t,
  NS: ns,
}: U3125CreditApplicationEditDlgProps) {
  const {
    formStateHandlers,
    onDlgClose,
    isDialogOpen,
    dialogBaselineAmount,
    dialogBaselineTaxable,
    dialogBaselineNonTaxable,
    dialogOpenSourceId,
    dialogEditingRowId,
  } = u3125CreditApplicationHandler;
  const draft = formStateHandlers.getData();
  const invoice = (parentFormHandlers.getData?.() ?? null) as U3125InvoiceFormRow | null;

  const availableSources = useMemo(
    () => u3125MergeCreditSourcesForDisplay(invoice?.availableCreditSources ?? [], invoice?.creditApplications ?? []),
    [invoice?.availableCreditSources, invoice?.creditApplications],
  );

  const parentReadOnly = parentFormHandlers.getFormState?.() === U2000FormStateEnumState.READ;
  const nestedNone = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.NONE;
  const canEdit = !parentReadOnly && u3125CanModifyInvoice(invoice?.status);
  const readOnly = nestedNone || !canEdit;

  const editingSourceId = draft?.sourceInvoiceId?.trim() ?? "";
  const comboSources = useMemo(
    () =>
      u3125SelectableCreditSourcesForDialog(
        availableSources,
        invoice?.creditApplications ?? [],
        formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT ? editingSourceId : undefined,
      ),
    [availableSources, invoice?.creditApplications, formStateHandlers, editingSourceId],
  );

  const selectedSource = useMemo(
    () => availableSources.find((source) => source.invoiceId === editingSourceId),
    [availableSources, editingSourceId],
  );

  const isCredit = selectedSource?.nature === "CREDIT";
  const sameSource = editingSourceId === dialogOpenSourceId;
  const editingId = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT ? dialogEditingRowId : undefined;

  const maxApply = u3125MaxCreditApplicationAmountForRow(
    selectedSource,
    sameSource ? dialogBaselineAmount : 0,
  );
  const maxTaxable = u3125MaxCreditApplicationTaxableForRow(
    selectedSource,
    sameSource ? dialogBaselineTaxable : 0,
    invoice,
    editingId,
  );
  const maxNonTaxable = u3125MaxCreditApplicationNonTaxableForRow(
    selectedSource,
    sameSource ? dialogBaselineNonTaxable : 0,
    invoice,
    editingId,
  );

  const amountReadOnly = readOnly || !editingSourceId;
  const totalHt = u3125RoundTaxAmount(
    Math.abs(Number(draft?.amountTaxable) || 0) + Math.abs(Number(draft?.amountNonTaxable) || 0),
  );

  useEffect(() => {
    if (amountReadOnly || !isCredit) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    let taxable = Math.abs(Number(row.amountTaxable) || 0);
    let nonTaxable = Math.abs(Number(row.amountNonTaxable) || 0);
    let changed = false;
    if (maxTaxable > 0 && taxable > maxTaxable + 0.005) {
      taxable = maxTaxable;
      changed = true;
    }
    if (maxNonTaxable > 0 && nonTaxable > maxNonTaxable + 0.005) {
      nonTaxable = maxNonTaxable;
      changed = true;
    }
    if (changed) {
      formStateHandlers.setData({
        ...row,
        amountTaxable: taxable,
        amountNonTaxable: nonTaxable,
        amount: u3125RoundTaxAmount(taxable + nonTaxable),
      });
    }
  }, [amountReadOnly, isCredit, maxTaxable, maxNonTaxable, editingSourceId, formStateHandlers]);

  useEffect(() => {
    if (amountReadOnly || isCredit || maxApply <= 0) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    const current = Math.abs(Number(row.amount) || 0);
    if (current > maxApply + 0.005) {
      formStateHandlers.setData({ ...row, amount: maxApply });
    }
  }, [amountReadOnly, isCredit, maxApply, editingSourceId, formStateHandlers]);

  const handleClose = (result: string) => {
    onDlgClose(result);
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      title={t("sectionCreditApplications", { ns })}
      isValid={true}
      readOnly={readOnly}
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBox
          formStateHandlers={formStateHandlers}
          fieldName="sourceInvoiceId"
          label={t("lblCreditApplicationSource", { ns })}
          data={comboSources}
          keyField="invoiceId"
          descField="invoiceId"
          readonly={readOnly}
          required={true}
          noneItemLabel={t("lblSelect", { ns })}
          getValue={(item: U3125CreditSource) => item.invoiceId}
          findItemByValue={(value, list) => {
            if (value == null || value === "") return null;
            return list.find((item) => item.invoiceId === String(value)) ?? null;
          }}
          formatLabel={(item: U3125CreditSource) => u3125FormatCreditSourceLabel(item, t, ns)}
        />

        {isCredit ? (
          <>
            <div className="space-y-1.5">
              <div className="flex min-h-5 items-baseline justify-between gap-3">
                <U2000Label label={t("lblCreditApplicationAmountTaxable", { ns })} htmlFor="amountTaxable" />
                {selectedSource && maxTaxable > 0 ? (
                  <span className="shrink-0 text-right text-xs font-medium leading-tight text-muted-foreground">
                    {t("lblCreditApplicationMaxTaxable", { ns, max: u3125FormatCurrency(maxTaxable) })}
                  </span>
                ) : null}
              </div>
              <U2000InputCurrency
                formStateHandlers={formStateHandlers}
                fieldName="amountTaxable"
                label=""
                readonly={amountReadOnly}
                decimalDigits={2}
                allowNegative={false}
                maxValue={maxTaxable > 0 ? maxTaxable : undefined}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex min-h-5 items-baseline justify-between gap-3">
                <U2000Label label={t("lblCreditApplicationAmountNonTaxable", { ns })} htmlFor="amountNonTaxable" />
                {selectedSource && maxNonTaxable > 0 ? (
                  <span className="shrink-0 text-right text-xs font-medium leading-tight text-muted-foreground">
                    {t("lblCreditApplicationMaxNonTaxable", { ns, max: u3125FormatCurrency(maxNonTaxable) })}
                  </span>
                ) : null}
              </div>
              <U2000InputCurrency
                formStateHandlers={formStateHandlers}
                fieldName="amountNonTaxable"
                label=""
                readonly={amountReadOnly}
                decimalDigits={2}
                allowNegative={false}
                maxValue={maxNonTaxable > 0 ? maxNonTaxable : undefined}
              />
            </div>
            <div className="space-y-1">
              <U2000Label label={t("lblCreditApplicationTotalHt", { ns })} />
              <p className="text-sm font-medium tabular-nums">{u3125FormatCurrency(totalHt)}</p>
            </div>
            {selectedSource ? (
              <p className="text-xs text-muted-foreground">
                {t("lblCreditApplicationRemainingBeforeTax", {
                  ns,
                  amount: u3125FormatCurrency(
                    u3125FormatBillingSourceDisplayAmount(selectedSource.remainingAmount, "CREDIT"),
                  ),
                })}
              </p>
            ) : null}
          </>
        ) : (
          <div className="space-y-1.5">
            <div className="flex min-h-5 items-baseline justify-between gap-3">
              <U2000Label label={t("lblCreditApplicationAmount", { ns })} htmlFor="amount" />
              {selectedSource && maxApply > 0 ? (
                <span className="shrink-0 text-right text-xs font-medium leading-tight text-muted-foreground">
                  {t("lblCreditApplicationMax", { ns, max: u3125FormatCurrency(maxApply) })}
                </span>
              ) : null}
            </div>
            <U2000InputCurrency
              formStateHandlers={formStateHandlers}
              fieldName="amount"
              label=""
              readonly={amountReadOnly}
              decimalDigits={2}
              allowNegative={false}
              maxValue={maxApply > 0 ? maxApply : undefined}
            />
          </div>
        )}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3125CreditApplicationEditDlg;
